import { Loader2 } from "lucide-react";

export const API = process.env.REACT_APP_BACKEND_URL;

export const apiFetch = async (path, options = {}) => {
    return fetch(`${API}/api${path}`, {
        credentials: "include",
        ...options,
    });
};

export const formatApiError = (detail, fallback = "Something went wrong. Please try again.") => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
    if (detail && typeof detail === "object") return Object.values(detail).flat().join(" ");
    return fallback;
};

export const formatDate = (iso, withTime = false) => {
    if (!iso) return "—";
    const options = { day: "numeric", month: "short", year: "numeric" };
    if (withTime) Object.assign(options, { hour: "2-digit", minute: "2-digit" });
    return new Date(iso).toLocaleDateString("en-GB", options);
};

export const VERIFICATION_LABELS = {
    pending: "Pending",
    under_review: "Under Review",
    verified: "Verified",
    rejected: "Rejected",
    suspended: "Suspended",
};

export const VERIFICATION_STYLES = {
    pending: "border-champagne/50 bg-champagne/10 text-[#8a7320]",
    under_review: "border-champagne/60 bg-champagne/15 text-[#8a7320]",
    verified: "border-forest/40 bg-forest/10 text-forest",
    rejected: "border-red-900/20 bg-red-900/5 text-red-900",
    suspended: "border-ink/20 bg-ink/5 text-ink/45",
};

export const JOB_STATUS_LABELS = {
    draft: "Draft",
    pending_review: "Pending Review",
    published: "Published",
    paused: "Paused",
    closed: "Closed",
    rejected: "Rejected",
    archived: "Archived",
};

export const JOB_STATUS_STYLES = {
    draft: "border-ink/20 bg-ink/[0.03] text-ink/50",
    pending_review: "border-champagne/60 bg-champagne/10 text-[#8a7320]",
    published: "border-forest/40 bg-forest/10 text-forest",
    paused: "border-ink/20 bg-sand text-ink/55",
    closed: "border-ink/15 bg-ink/[0.03] text-ink/40",
    rejected: "border-red-900/20 bg-red-900/5 text-red-900",
    archived: "border-ink/10 bg-ink/[0.03] text-ink/35",
};

export const APPLICATION_STATUS_LABELS = {
    submitted: "Submitted",
    new: "Submitted",
    under_review: "Under Review",
    shortlisted: "Shortlisted",
    interview: "Interview",
    selected: "Selected",
    offer: "Offer",
    documentation: "Documentation",
    visa_processing: "Visa Processing",
    placement: "Placement",
    rejected: "Rejected",
    withdrawn: "Withdrawn",
    hired: "Hired",
};

export const APPLICATION_STATUS_STYLES = {
    submitted: "border-forest/30 bg-forest/5 text-forest",
    new: "border-forest/30 bg-forest/5 text-forest",
    under_review: "border-ink/20 bg-ink/[0.03] text-ink/55",
    shortlisted: "border-champagne/60 bg-champagne/10 text-[#8a7320]",
    interview: "border-champagne/60 bg-champagne/15 text-[#8a7320]",
    selected: "border-forest/40 bg-forest/10 text-forest",
    offer: "border-forest/40 bg-forest/10 text-forest",
    documentation: "border-ink/20 bg-sand text-ink/60",
    visa_processing: "border-ink/20 bg-sand text-ink/60",
    placement: "border-forest bg-forest text-ivory",
    rejected: "border-red-900/20 bg-red-900/5 text-red-900",
    withdrawn: "border-ink/15 bg-ink/[0.03] text-ink/40",
    hired: "border-forest bg-forest text-ivory",
};

export const StatusPill = ({ status, type = "job", dark = false }) => {
    const labels = type === "verification" ? VERIFICATION_LABELS : type === "application" ? APPLICATION_STATUS_LABELS : JOB_STATUS_LABELS;
    const styles = type === "verification" ? VERIFICATION_STYLES : type === "application" ? APPLICATION_STATUS_STYLES : JOB_STATUS_STYLES;
    const darkVerification = {
        pending: "border-champagne/40 bg-champagne/10 text-champagne",
        under_review: "border-champagne/50 bg-champagne/15 text-champagne",
        verified: "border-champagne/60 bg-champagne/15 text-champagne",
        rejected: "border-red-300/30 bg-red-300/10 text-red-100",
        suspended: "border-ivory/20 bg-ivory/5 text-ivory/60",
    };
    const selectedStyles = dark && type === "verification" ? darkVerification : styles;
    return (
        <span data-testid={`status-pill-${status}`} className={`inline-flex w-fit items-center whitespace-nowrap border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${selectedStyles[status] || selectedStyles.draft || selectedStyles.pending}`}>
            {labels[status] || status}
        </span>
    );
};

export const inputClass =
    "w-full border border-ink/15 bg-ivory px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink/35 focus:border-forest";

export const Label = ({ children }) => (
    <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-ink/55">{children}</span>
);

export const Kicker = ({ children, dark = false }) => (
    <p className={`flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] ${dark ? "text-champagne" : "text-forest"}`}>
        <span className="h-px w-7 bg-champagne" aria-hidden="true" />
        {children}
    </p>
);

export const PrimaryButton = ({ busy, children, className = "", ...props }) => (
    <button
        type="button"
        {...props}
        disabled={busy || props.disabled}
        className={`inline-flex items-center justify-center gap-2 whitespace-nowrap bg-forest px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors duration-300 hover:bg-forest-deep disabled:cursor-not-allowed disabled:opacity-70 ${className}`}
    >
        {busy && <Loader2 size={14} className="animate-spin" />}
        {children}
    </button>
);

export const SecondaryButton = ({ children, className = "", ...props }) => (
    <button
        type="button"
        {...props}
        className={`inline-flex items-center justify-center gap-2 whitespace-nowrap border border-ink/20 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink/65 transition-colors duration-300 hover:border-forest hover:text-forest disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
        {children}
    </button>
);

export const EmptyState = ({ title, copy, action }) => (
    <div data-testid="empty-state" className="border border-dashed border-ink/20 bg-sand/60 px-6 py-14 text-center">
        <p className="font-serif text-2xl font-medium tracking-tight text-ink">{title}</p>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink/55">{copy}</p>
        {action && <div className="mt-6">{action}</div>}
    </div>
);

export const Spinner = () => (
    <div className="flex justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-forest border-t-transparent" />
    </div>
);
